import type {
    StoryboardListData,
    UpdateStoryboardCellParams,
    UpdateStoryboardCellData,
    CopyStoryboardParams,
    CopyStoryboardData,
    DeleteStoryboardParams,
    DeleteStoryboardMaterialParams,
    DeleteStoryboardMaterialData,
    RemarkMaterialParams,
    RemarkMaterialData,
    RetryPromptGenParams,
    RetryPromptGenData,
    ReorderStoryboardData,
    ReorderStoryboardParams,
    CreateStoryboardParams,
    StoryboardInfoParams,
    StoryboardInfoData,
    SaveColumnSettingsParams,
} from './interface';
import type {ProjectId} from '@/types';
import http from '@/services/request';

/** 分镜相关接口 URL */
const urls = {
    /** 获取项目分集下的分镜列表 */
    storyboardList: 'proxy-aicomic/storyboard/list',
    /** 修改分镜单元格内容 */
    updateStoryboardCell: 'proxy-aicomic/storyboard/update',
    /** 从已有分镜复制一个新分镜 */
    copyStoryboard: 'proxy-aicomic/storyboard/copy',
    /** 删除指定分镜 */
    deleteStoryboard: 'proxy-aicomic/storyboard/delete',
    /** 删除分镜资源 */
    deleteStoryboardMaterial: 'proxy-aicomic/storyboard/material/delete',
    /** 分镜素材批注 */
    remarkMaterial: 'proxy-aicomic/storyboard/material/comment',
    /** 提示词生成重试 */
    retryPromptGen: 'proxy-aicomic/storyboard/prompt/retry',
    /** 分镜拖拽重排序 */
    reorderStoryboard: 'proxy-aicomic/storyboard/reorder',
    /** 新增空分镜 */
    createStoryboard: 'proxy-aicomic/storyboard/create',
    /** 单个分镜信息 */
    storyboardInfo: 'proxy-aicomic/storyboard/info',
    /** 保存用户列展示设置 */
    saveColumnSettings: 'proxy-aicomic/storyboard/columnconfig',
} as const;

/**
 * 获取项目分集下的分镜列表
 * GET /proxy-aicomic/api/projects/episodes/storyboards
 * episodeId 放宽签名，缺少时由后端报错，不传兜底值。
 */
export const getStoryboardList = (projectId: ProjectId, episodeId?: number) =>
    http.get<StoryboardListData>(urls.storyboardList, {
        params: {projectId, episodeId},
    });

/**
 * 修改分镜单元格内容
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4841983
 */
export const updateStoryboardCell = (data: UpdateStoryboardCellParams) =>
    http.post<UpdateStoryboardCellData>(urls.updateStoryboardCell, data);

/**
 * 从已有分镜复制一个新分镜
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4841962
 */
export const copyStoryboard = (data: CopyStoryboardParams) =>
    http.post<CopyStoryboardData>(urls.copyStoryboard, data);

/**
 * 删除指定分镜
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4841914
 */
export const deleteStoryboard = (data: DeleteStoryboardParams) =>
    http.post(urls.deleteStoryboard, data);
/**
 * 删除分镜资源
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4842115
 */
export const deleteStoryboardMaterial = (data: DeleteStoryboardMaterialParams) =>
    http.post<DeleteStoryboardMaterialData>(urls.deleteStoryboardMaterial, data);

/**
 * 分镜素材批注
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4842120
 */
export const remarkMaterial = (data: RemarkMaterialParams) =>
    http.post<RemarkMaterialData>(urls.remarkMaterial, data);

/**
 * 提示词生成重试
 * POST /proxy-aicomic/storyboard/prompt/retry
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4868952
 */
export const retryPromptGen = (data: RetryPromptGenParams) =>
    http.post<RetryPromptGenData>(urls.retryPromptGen, data);

/**
 * 分镜拖拽重排序
 * POST /proxy-aicomic/storyboard/reorder
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4841915
 */
export const reorderStoryboard = (data: ReorderStoryboardParams) =>
    http.post<ReorderStoryboardData>(urls.reorderStoryboard, data);

/**
 * 新增空分镜（指定位置）
 * POST /proxy-aicomic/storyboard/create
 * https://iapi.baidu-int.com/web/project/367583/apis/api-4841913
 */
export const createStoryboard = (data: CreateStoryboardParams) =>
    http.post<ReorderStoryboardData>(urls.createStoryboard, data);

/**
 * 单个分镜信息
 * POST /proxy-aicomic/storyboard/info
 */
export const getStoryboardInfo = (data: StoryboardInfoParams) =>
    http.post<StoryboardInfoData>(urls.storyboardInfo, data);

/**
 * 保存用户列展示设置（upsert，个人 + 分集粒度）
 * POST /proxy-aicomic/storyboard/columnconfig
 */
export const saveColumnSettings = (data: SaveColumnSettingsParams) =>
    http.post(urls.saveColumnSettings, data);
